import { useEffect, useState } from 'react';
import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import { tableFiltersVM, tableStateVM, tableStatsVM, tableViewVM } from '../../../../core/table/table_view_models';
import {
    cpOrderInvoicesTableStateAtom,
    cpOrderInvoicesTableViewAtom,
    cpOrderInvoicesDataAtom,
    cpOrderInvoicesTableStatsAtom,
    cpOrderInvoicesTableFiltersAtom,
    setAtomCpOrderInvoicesListTableViewState,
    cpOrderInvoicesSearchFormAtom,
} from '../cp_order_invoices_list_atoms';
import { cpOrderInvoicesListColumnSpec } from './CpOrderInvoicesListTableSpec';
import { getCpOrderInvoicesInitialView } from './getCpOrderInvoicesInitialView';
import viewStateRepo from '../../../../core/repo/viewStateRepo';
import { tableDataVM } from '../../../../core/table/table_view_models';
import { CpOrderInvoicesDataItem } from '../cp_order_invoices_list_types';
import { ListTableInitHookReturn } from '../../../../core/table/table.types';
import { useCpOrderInvoicesStore } from '../pagehooks/useCpOrderInvoicesStore';
import { initializeSessionViewsStorage } from '../../../../core/table/tableViewsRepo';
import { CP_ORDER_INVOICES_LIST_STORAGE_KEY, CP_ORDER_INVOICES_LIST_VIEW_KEY } from '../../cp_order_invoices_const';
import { useAtomValue } from 'jotai';
import { cpOrderInvoicesUserImpersonateAtom } from '../cp_order_invoices_list_atoms';

export const useCpOrderInvoicesListTableInit = (): ListTableInitHookReturn => {
    const [init, setInit] = useState(true);
    const pageStore = useCpOrderInvoicesStore();
    const searchState = useAtomValue(cpOrderInvoicesSearchFormAtom);
    const userImpersonate = useAtomValue(cpOrderInvoicesUserImpersonateAtom);
    const [_, setState] = useImmerAtom(cpOrderInvoicesTableStateAtom);
    const setView = useSetImmerAtom(cpOrderInvoicesTableViewAtom);
    const setList = useSetImmerAtom(cpOrderInvoicesDataAtom);
    const setStats = useSetImmerAtom(cpOrderInvoicesTableStatsAtom);
    const setFilter = useSetImmerAtom(cpOrderInvoicesTableFiltersAtom);

    // INIT
    useEffect(() => {
        // Use centralized user context from page state atom
        const initializeAsync = async (): Promise<void> => {
            // First ensure session storage is populated from database if needed
            try {
                await initializeSessionViewsStorage(CP_ORDER_INVOICES_LIST_VIEW_KEY);
            } catch (error) {
                console.warn('⚠️ Failed to initialize session storage:', error);
            }
        };

        setState(draft => {
            if (init && !draft.waiting) {
                setInit(false);
                // Initialize session storage asynchronously (non-blocking)
                initializeAsync();

                try {
                    const colSpec = cpOrderInvoicesListColumnSpec();
                    const currentViewSpec = draft.views.find(v => v.key === draft.currentViewId);
                    if (currentViewSpec && typeof currentViewSpec.pgsize === 'number' && currentViewSpec.pgsize > 0) {
                        setStats(draftStats => {
                            draftStats.paging.size =
                                typeof currentViewSpec.pgsize === 'number' ? currentViewSpec.pgsize : 50;
                        });
                    }

                    const viewstate = viewStateRepo.getTable(
                        CP_ORDER_INVOICES_LIST_STORAGE_KEY,
                        Array.from(colSpec.keys()),
                    );
                    if (
                        !!viewstate &&
                        viewstate.sort != null &&
                        typeof viewstate.sort.col === 'string' &&
                        viewstate.sort.col !== '' &&
                        typeof viewstate.sort.dir === 'string' &&
                        viewstate.sort.dir !== ''
                    ) {
                        setView(draft => {
                            draft.sort = viewstate.sort;
                        });
                    }
                    if (!!viewstate) {
                        setFilter(() => viewstate.flt);
                        setStats(draft => {
                            draft.paging = viewstate.paging;
                        });
                    }

                    if (viewstate && !userImpersonate) {
                        pageStore.getList();
                    }
                } catch (error) {
                    console.error('❌ Error during table initialization:', error);
                    // BULLETPROOF: Fall back to guaranteed working configuration
                    const colSpec = cpOrderInvoicesListColumnSpec();
                    const defaultView = getCpOrderInvoicesInitialView();

                    // Ensure the fallback default view has ALL available columns
                    const allColumnKeys = Array.from(colSpec.keys());
                    const enhancedDefaultView = { ...defaultView };

                    // Make sure all columns are represented in sections
                    const existingCols = enhancedDefaultView.sections.flatMap(s => s.cols || []);
                    const missingCols = allColumnKeys.filter(key => !existingCols.includes(key));

                    if (missingCols.length > 0 && enhancedDefaultView.sections.length > 0) {
                        console.warn('🛡️ Adding missing columns to default view:', missingCols);
                        // Add missing columns to the first section
                        if (enhancedDefaultView.sections.length > 0) {
                            enhancedDefaultView.sections[0].cols.push(...missingCols);
                        }
                    }

                    draft.views = [enhancedDefaultView];
                    draft.currentViewId = enhancedDefaultView.key;

                    const fallbackView = tableViewVM();
                    fallbackView.cols = Array.from(colSpec.values());
                    setView(() => fallbackView);
                }
                return draft;
            } else if (draft.init && draft.waiting && !pageStore.loading) {
                draft.init = false;
                draft.waiting = false;
                return draft;
            } else {
                return draft;
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [init, pageStore, searchState, setState, setView, setFilter, setStats, setList]);

    useEffect(() => {
        return (): void => {
            setAtomCpOrderInvoicesListTableViewState();
            setState(() => tableStateVM());
            setView(() => tableViewVM());
            setFilter(() => tableFiltersVM());
            setStats(() => tableStatsVM());
            setList(() => tableDataVM<CpOrderInvoicesDataItem>());
        };
    }, [setFilter, setList, setState, setStats, setView]);

    return { init };
};
